import React, { useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../css/ContactUs.css';
import '../css/about.css';

const Bits = () => {
  const underlineRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (underlineRef.current) {
        underlineRef.current.classList.add('animate');
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  const bitTypes = [
    {
      icon: 'bi bi-gem',
      color: '#0c61c0',
      title: 'Premium PDC Bits',
      desc: 'High-performance polycrystalline diamond compact bits built for demanding sections — engineered cutter layouts and abrasion-resistant grades that hold ROP through hard and interbedded formations.',
    },
    {
      icon: 'bi bi-speedometer2',
      color: '#28a745',
      title: 'Standard PDC Bits',
      desc: 'Balanced PDC bits for routine drilling programmes, offering dependable footage and stable directional behaviour across soft to medium formations.',
    },
    {
      icon: 'bi bi-cash-coin',
      color: '#fd7e14',
      title: 'Economy PDC Bits',
      desc: 'Cost-effective PDC options for shallow sections, top-hole work, and high-volume drilling where cost per metre matters more than peak specification.',
    },
    {
      icon: 'bi bi-bullseye',
      color: '#dc3545',
      title: 'Impregnated Diamond Bits',
      desc: 'Diamond-impregnated matrix bits for very hard and highly abrasive formations, delivering long bit life where conventional cutters wear out quickly.',
    },
    {
      icon: 'bi bi-brilliance',
      color: '#6f42c1',
      title: 'Natural Diamond Bits',
      desc: 'Surface-set natural diamond bits for hard, non-brittle formations and specialised applications requiring a fine, controlled cutting action.',
    },
    {
      icon: 'bi bi-circle-square',
      color: '#17a2b8',
      title: 'Coring Bits',
      desc: 'Core heads designed to recover intact formation samples for reservoir evaluation, with cutting structures matched to the formation and core barrel system in use.',
    },
    {
      icon: 'bi bi-nut',
      color: '#20c997',
      title: 'Frac Plug Mill Bits',
      desc: 'Purpose-built mill bits for drilling out composite frac plugs during completion clean-out runs, sized for fast, controlled removal without damaging casing.',
    },
    {
      icon: 'bi bi-diagram-2',
      color: '#6c757d',
      title: 'Dual-Bore & Hybrid Bits',
      desc: 'Dual-bore and dual cutting structure designs that combine cutter types or bore diameters in a single body to handle changing formations in one run.',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <div className="container-fluid about-bg d-flex align-items-end" style={{ minHeight: '85vh', padding: 0, top: '-122px' }}>
        <div className="container about-content">
          <div className="row">
            <div className="col-lg-6 col-md-8 col-12 about-text">
              <h1 className="about-title text-white">Drill Bits</h1>
              <h2 className="about-subtitle">PDC, Impregnated, Natural Diamond &amp; Specialty Bits</h2>
              <div className="about-underline" ref={underlineRef}></div>
              <p className="about-desc text-white" style={{ textAlign: 'justify' }}>
                Petrochoice supplies a complete range of diamond drilling bits for oil, gas, and geothermal
                operations — from premium PDC bits for demanding production sections to impregnated and
                natural diamond bits for the hardest formations, plus coring heads and frac plug mills.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* What We Offer */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="row g-5 align-items-center">
            <div className="col-12">
              <h2 className="fw-bold mb-4" style={{ color: '#0c61c0' }}>What We Offer</h2>
              <p className="mb-4">
                Bit selection drives cost per metre more than almost any other single choice in a drilling
                programme. Petrochoice supplies bits across every major cutting technology — polycrystalline
                diamond compact (PDC), diamond-impregnated matrix, and surface-set natural diamond — so the
                bit can be matched to the formation rather than the formation worked around the bit.
              </p>
              <p className="mb-0">
                Our range covers standard hole sizes through to specialty applications: coring heads for
                formation evaluation, frac plug mills for completion clean-out, and dual-bore and hybrid
                cutting structures for sections where formation hardness changes mid-run.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bit Range */}
      <div className="container-xxl py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <h2 className="fw-bold mb-5 text-center" style={{ color: '#0c61c0' }}>Our Bit Range</h2>
          <div className="row g-4">
            {bitTypes.map((b) => (
              <div className="col-lg-3 col-md-6" key={b.title}>
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <i className={`${b.icon} fs-2`} style={{ color: b.color }}></i>
                    <h3 className="h6 fw-bold mt-3 mb-2">{b.title}</h3>
                    <p className="mb-0 text-muted small">{b.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How We Work */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="row g-5 align-items-center">
            <div className="col-lg-4">
              <img
                src={require('../assets/level2-600x402.jpg')}
                alt="Bit Selection Support"
                className="img-fluid rounded shadow"
              />
            </div>
            <div className="col-lg-8">
              <h2 className="fw-bold mb-4" style={{ color: '#0c61c0' }}>Application Engineering</h2>
              <p className="mb-4">
                We do not sell bits from a catalogue alone. Our team reviews offset well data, formation
                logs, and planned well profile to recommend the cutting structure, gauge design, and
                hydraulics configuration that suit the section — then reviews dull grading after the run to
                refine the choice for the next well.
              </p>
              <ul className="list-group list-group-flush">
                <li className="list-group-item px-0"><i className="bi bi-clipboard-data text-primary me-2"></i>Offset data review and bit selection</li>
                <li className="list-group-item px-0"><i className="bi bi-rulers text-success me-2"></i>Hydraulics and nozzle configuration</li>
                <li className="list-group-item px-0"><i className="bi bi-search text-info me-2"></i>Post-run dull grading and analysis</li>
                <li className="list-group-item px-0"><i className="bi bi-arrow-repeat text-warning me-2"></i>Repair, re-tip, and refurbishment options</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Bits;
